import React, { useMemo, useState } from "react";
import {
    Search,
    Filter,
    Eye,
    CheckCircle2,
    XCircle,
    Clock3,
    Users,
    ShieldCheck,
    X,
    MapPin,
    Phone,
    Mail,
    CalendarDays,
    UserRound,
    FileCheck2,
    AlertTriangle,
    Check,
    RotateCcw,
} from "lucide-react";

/* =========================================================
   SAMPLE DATA
========================================================= */

const initialProfiles = [
    {
        id: 1,
        memberId: "JP00001",
        name: "Arun Kumar",
        gender: "Male",
        mobile: "9876543210",
        email: "arun@gmail.com",
        branch: "Erode Main Branch",
        community: "Kongu Vellalar",
        dob: "1993-06-12",
        profileCompletion: 92,

        aadhaarLast4: "1234",
        aadhaarStatus: "Pending",
        aadhaarVerifiedDate: "",

        profileStatus: "Pending",

        aadhaarDocument:
            "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1000",

        profileCreatedDate: "2026-09-28",
        submittedDate: "2026-10-02",

        verificationHistory: [
            {
                date: "2026-10-02",
                action: "Profile Submitted",
                by: "Arun Kumar",
                status: "Pending",
            },
        ],
    },

    {
        id: 2,
        memberId: "JP00002",
        name: "Priya S",
        gender: "Female",
        mobile: "9876543211",
        email: "priya@gmail.com",
        branch: "Erode Main Branch",
        community: "Kongu Vellalar",
        dob: "1997-03-21",
        profileCompletion: 98,

        aadhaarLast4: "5678",
        aadhaarStatus: "Verified",
        aadhaarVerifiedDate: "2026-09-30",

        profileStatus: "Verified",

        aadhaarDocument:
            "https://images.unsplash.com/photo-1568234928966-359c35dd8327?w=1000",

        profileCreatedDate: "2026-09-25",
        submittedDate: "2026-09-29",

        verificationHistory: [
            {
                date: "2026-09-30",
                action: "Aadhaar Verified",
                by: "Admin",
                status: "Verified",
            },
            {
                date: "2026-09-30",
                action: "Profile Approved",
                by: "Admin",
                status: "Verified",
            },
        ],
    },

    {
        id: 3,
        memberId: "JP00003",
        name: "Karthik R",
        gender: "Male",
        mobile: "9876543212",
        email: "karthik@gmail.com",
        branch: "Erode Main Branch",
        community: "Gounder",
        dob: "1990-11-05",
        profileCompletion: 84,

        aadhaarLast4: "9012",
        aadhaarStatus: "Rejected",
        aadhaarVerifiedDate: "",

        profileStatus: "Rejected",

        aadhaarDocument:
            "https://images.unsplash.com/photo-1554224154-22dec7ec8818?w=1000",

        profileCreatedDate: "2026-09-22",
        submittedDate: "2026-09-27",

        rejectionReason:
            "Aadhaar document image is not clearly visible.",

        verificationHistory: [
            {
                date: "2026-09-28",
                action: "Aadhaar Verification Rejected",
                by: "Admin",
                status: "Rejected",
            },
        ],
    },

    {
        id: 4,
        memberId: "JP00004",
        name: "Divya M",
        gender: "Female",
        mobile: "9876543213",
        email: "divya@gmail.com",
        branch: "Erode Main Branch",
        community: "Kongu Vellalar",
        dob: "1995-08-18",
        profileCompletion: 95,

        aadhaarLast4: "3456",
        aadhaarStatus: "Pending",
        aadhaarVerifiedDate: "",

        profileStatus: "Pending",

        aadhaarDocument:
            "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=1000",

        profileCreatedDate: "2026-09-30",
        submittedDate: "2026-10-01",

        verificationHistory: [
            {
                date: "2026-10-01",
                action: "Profile Submitted",
                by: "Divya M",
                status: "Pending",
            },
        ],
    },

    {
        id: 5,
        memberId: "JP00005",
        name: "Suresh Kumar",
        gender: "Male",
        mobile: "9876543214",
        email: "suresh@gmail.com",
        branch: "Erode Main Branch",
        community: "Nadar",
        dob: "1992-04-11",
        profileCompletion: 88,

        aadhaarLast4: "7890",
        aadhaarStatus: "Verified",
        aadhaarVerifiedDate: "2026-09-28",

        profileStatus: "Verified",

        aadhaarDocument:
            "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1000",

        profileCreatedDate: "2026-09-20",
        submittedDate: "2026-09-25",

        verificationHistory: [
            {
                date: "2026-09-28",
                action: "Aadhaar Verified",
                by: "Admin",
                status: "Verified",
            },
            {
                date: "2026-09-28",
                action: "Profile Approved",
                by: "Admin",
                status: "Verified",
            },
        ],
    },
];

/* =========================================================
   STATUS OPTIONS
========================================================= */

const statusOptions = [
    "Pending",
    "Verified",
    "Rejected",
];

function ProfileVerification() {
    const [profiles, setProfiles] =
        useState(initialProfiles);

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] =
        useState("");

    const [showFilters, setShowFilters] =
        useState(false);

    const [selectedProfile, setSelectedProfile] =
        useState(null);

    const [showModal, setShowModal] =
        useState(false);

    const [showDocument, setShowDocument] =
        useState(false);

    const [rejectionReason, setRejectionReason] =
        useState("");

    const [showRejectBox, setShowRejectBox] =
        useState(false);

    /* =====================================================
       SUMMARY
    ===================================================== */

    const totalProfiles = profiles.length;

    const pendingProfiles = profiles.filter(
        (item) =>
            item.profileStatus === "Pending"
    ).length;

    const verifiedProfiles = profiles.filter(
        (item) =>
            item.profileStatus === "Verified"
    ).length;

    const rejectedProfiles = profiles.filter(
        (item) =>
            item.profileStatus === "Rejected"
    ).length;

    /* =====================================================
       FILTER
    ===================================================== */

    const filteredProfiles = useMemo(() => {
        const text = search
            .trim()
            .toLowerCase();

        return profiles.filter((profile) => {
            const matchesSearch =
                !text ||
                profile.memberId
                    .toLowerCase()
                    .includes(text) ||
                profile.name
                    .toLowerCase()
                    .includes(text) ||
                profile.mobile
                    .toLowerCase()
                    .includes(text);

            const matchesStatus =
                !statusFilter ||
                profile.profileStatus ===
                    statusFilter;

            return (
                matchesSearch &&
                matchesStatus
            );
        });
    }, [profiles, search, statusFilter]);

    /* =====================================================
       OPEN VIEW
    ===================================================== */

    const openView = (profile) => {
        setSelectedProfile(profile);

        setRejectionReason(
            profile.rejectionReason || ""
        );

        setShowRejectBox(false);
        setShowDocument(false);
        setShowModal(true);
    };

    /* =====================================================
       CLOSE
    ===================================================== */

    const closeModal = () => {
        setShowModal(false);
        setSelectedProfile(null);
        setShowDocument(false);
        setShowRejectBox(false);
    };

    /* =====================================================
       VERIFY AADHAAR
    ===================================================== */

    const verifyAadhaar = () => {
        if (!selectedProfile) return;

        const today = new Date()
            .toISOString()
            .split("T")[0];

        const updatedProfile = {
            ...selectedProfile,
            aadhaarStatus: "Verified",
            aadhaarVerifiedDate: today,
            profileStatus: "Verified",
            verificationHistory: [
                ...(selectedProfile.verificationHistory ||
                    []),
                {
                    date: today,
                    action: "Aadhaar Verified",
                    by: "Branch Admin",
                    status: "Verified",
                },
            ],
        };

        setProfiles((prev) =>
            prev.map((item) =>
                item.id ===
                selectedProfile.id
                    ? updatedProfile
                    : item
            )
        );

        setSelectedProfile(updatedProfile);
        setShowRejectBox(false);

        alert(
            "Aadhaar verified successfully."
        );
    };

    /* =====================================================
       REJECT
    ===================================================== */

    const rejectProfile = () => {
        if (!selectedProfile) return;

        if (!rejectionReason.trim()) {
            alert(
                "Please enter rejection reason."
            );
            return;
        }

        const today = new Date()
            .toISOString()
            .split("T")[0];

        const updatedProfile = {
            ...selectedProfile,
            aadhaarStatus: "Rejected",
            profileStatus: "Rejected",
            rejectionReason:
                rejectionReason.trim(),

            verificationHistory: [
                ...(selectedProfile.verificationHistory ||
                    []),
                {
                    date: today,
                    action:
                        "Profile Verification Rejected",
                    by: "Branch Admin",
                    status: "Rejected",
                },
            ],
        };

        setProfiles((prev) =>
            prev.map((item) =>
                item.id ===
                selectedProfile.id
                    ? updatedProfile
                    : item
            )
        );

        setSelectedProfile(updatedProfile);
        setShowRejectBox(false);

        alert(
            "Profile rejected successfully."
        );
    };

    /* =====================================================
       APPROVE PROFILE
    ===================================================== */

    const approveProfile = () => {
        if (!selectedProfile) return;

        if (
            selectedProfile.aadhaarStatus !==
            "Verified"
        ) {
            alert(
                "Please verify Aadhaar before approving the profile."
            );
            return;
        }

        const today = new Date()
            .toISOString()
            .split("T")[0];

        const updatedProfile = {
            ...selectedProfile,
            profileStatus: "Verified",
            verificationHistory: [
                ...(selectedProfile.verificationHistory ||
                    []),
                {
                    date: today,
                    action:
                        "Profile Approved",
                    by: "Branch Admin",
                    status: "Verified",
                },
            ],
        };

        setProfiles((prev) =>
            prev.map((item) =>
                item.id ===
                selectedProfile.id
                    ? updatedProfile
                    : item
            )
        );

        setSelectedProfile(updatedProfile);

        alert(
            "Profile approved successfully."
        );
    };

    /* =====================================================
       RESET FILTER
    ===================================================== */

    const resetFilters = () => {
        setSearch("");
        setStatusFilter("");
    };

    return (
        <div className="profile-verification-page">

            {/* =================================================
                HEADER
            ================================================= */}

            <div className="verification-page-header">

                <div>
                    <h1>
                        Profile Verification
                    </h1>

                    <p>
                        Review member profiles and
                        verify Aadhaar documents before
                        profile approval.
                    </p>
                </div>

            </div>

            {/* =================================================
                SECURITY NOTICE
            ================================================= */}

            <div className="verification-security-notice">

                <div className="security-notice-icon">
                    <ShieldCheck size={21} />
                </div>

                <div>
                    <strong>
                        Aadhaar Security
                    </strong>

                    <p>
                        Aadhaar numbers are masked in
                        the admin interface. Only the
                        minimum information required for
                        verification should be displayed
                        or stored.
                    </p>
                </div>

            </div>

            {/* =================================================
                SUMMARY
            ================================================= */}

            <div className="verification-summary-grid">

                <div className="verification-summary-card">

                    <div className="verification-summary-icon blue">
                        <Users size={22} />
                    </div>

                    <div>
                        <span>Total Profiles</span>
                        <strong>
                            {totalProfiles}
                        </strong>
                    </div>

                </div>

                <div className="verification-summary-card">

                    <div className="verification-summary-icon orange">
                        <Clock3 size={22} />
                    </div>

                    <div>
                        <span>Pending</span>
                        <strong>
                            {pendingProfiles}
                        </strong>
                    </div>

                </div>

                <div className="verification-summary-card">

                    <div className="verification-summary-icon green">
                        <CheckCircle2
                            size={22}
                        />
                    </div>

                    <div>
                        <span>Verified</span>
                        <strong>
                            {verifiedProfiles}
                        </strong>
                    </div>

                </div>

                <div className="verification-summary-card">

                    <div className="verification-summary-icon red">
                        <XCircle size={22} />
                    </div>

                    <div>
                        <span>Rejected</span>
                        <strong>
                            {rejectedProfiles}
                        </strong>
                    </div>

                </div>

            </div>

            {/* =================================================
                SEARCH / FILTER
            ================================================= */}

            <div className="verification-filter-card">

                <div className="verification-filter-top">

                    <div className="verification-search">

                        <Search size={18} />

                        <input
                            type="text"
                            value={search}
                            placeholder="Search Member ID, Name or Mobile Number..."
                            onChange={(e) =>
                                setSearch(
                                    e.target.value
                                )
                            }
                        />

                        {search && (
                            <button
                                type="button"
                                onClick={() =>
                                    setSearch("")
                                }
                            >
                                <X size={15} />
                            </button>
                        )}

                    </div>

                    <button
                        type="button"
                        className={`verification-filter-button ${
                            showFilters
                                ? "active"
                                : ""
                        }`}
                        onClick={() =>
                            setShowFilters(
                                (prev) => !prev
                            )
                        }
                    >
                        <Filter size={17} />
                        Filters
                    </button>

                </div>

                {showFilters && (
                    <div className="verification-filter-body">

                        <div className="verification-filter-field">

                            <label>
                                Verification Status
                            </label>

                            <select
                                value={statusFilter}
                                onChange={(e) =>
                                    setStatusFilter(
                                        e.target.value
                                    )
                                }
                            >
                                <option value="">
                                    All Status
                                </option>

                                {statusOptions.map(
                                    (item) => (
                                        <option
                                            key={item}
                                            value={item}
                                        >
                                            {item}
                                        </option>
                                    )
                                )}

                            </select>

                        </div>

                        <button
                            type="button"
                            className="verification-reset-btn"
                            onClick={
                                resetFilters
                            }
                        >
                            <RotateCcw
                                size={15}
                            />
                            Reset
                        </button>

                    </div>
                )}

            </div>

            {/* =================================================
                TABLE
            ================================================= */}

            <div className="verification-table-card">

                <div className="verification-table-header">

                    <div>
                        <h2>
                            Verification Queue
                        </h2>

                        <span>
                            {
                                filteredProfiles.length
                            } profiles found
                        </span>
                    </div>

                </div>

                <div className="verification-table-wrapper">

                    <table className="verification-table">

                        <thead>
                            <tr>
                                <th>S.No</th>
                                <th>Member</th>
                                <th>Mobile</th>
                                <th>Community</th>
                                <th>Aadhaar</th>
                                <th>Profile</th>
                                <th>Completion</th>
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>

                        <tbody>

                            {filteredProfiles.length ===
                            0 ? (
                                <tr>
                                    <td
                                        colSpan="9"
                                        className="verification-empty"
                                    >
                                        No verification
                                        records found.
                                    </td>
                                </tr>
                            ) : (
                                filteredProfiles.map(
                                    (
                                        profile,
                                        index
                                    ) => (
                                        <tr
                                            key={
                                                profile.id
                                            }
                                        >

                                            <td>
                                                {index +
                                                    1}
                                            </td>

                                            <td>
                                                <div className="verification-member">

                                                    <div className="verification-avatar">
                                                        <UserRound
                                                            size={
                                                                18
                                                            }
                                                        />
                                                    </div>

                                                    <div>
                                                        <strong>
                                                            {
                                                                profile.name
                                                            }
                                                        </strong>

                                                        <span>
                                                            {
                                                                profile.memberId
                                                            }
                                                        </span>
                                                    </div>

                                                </div>
                                            </td>

                                            <td>
                                                <div className="verification-mobile">
                                                    <Phone
                                                        size={
                                                            14
                                                        }
                                                    />
                                                    {
                                                        profile.mobile
                                                    }
                                                </div>
                                            </td>

                                            <td>
                                                {
                                                    profile.community
                                                }
                                            </td>

                                            <td>
                                                <div className="aadhaar-masked">
                                                    <ShieldCheck
                                                        size={
                                                            14
                                                        }
                                                    />

                                                    XXXX XXXX{" "}
                                                    {
                                                        profile.aadhaarLast4
                                                    }
                                                </div>
                                            </td>

                                            <td>
                                                <span
                                                    className={`verification-status-badge ${profile.profileStatus.toLowerCase()}`}
                                                >
                                                    <span />
                                                    {
                                                        profile.profileStatus
                                                    }
                                                </span>
                                            </td>

                                            <td>

                                                <div className="completion-cell">

                                                    <div className="completion-top">
                                                        <span>
                                                            {
                                                                profile.profileCompletion
                                                            }
                                                            %
                                                        </span>
                                                    </div>

                                                    <div className="completion-bar">
                                                        <div
                                                            style={{
                                                                width: `${profile.profileCompletion}%`,
                                                            }}
                                                        />
                                                    </div>

                                                </div>

                                            </td>

                                            <td>

                                                <span
                                                    className={`verification-status-badge ${profile.aadhaarStatus.toLowerCase()}`}
                                                >
                                                    <span />
                                                    {
                                                        profile.aadhaarStatus
                                                    }
                                                </span>

                                            </td>

                                            <td>

                                                <button
                                                    type="button"
                                                    className="verification-view-btn"
                                                    onClick={() =>
                                                        openView(
                                                            profile
                                                        )
                                                    }
                                                >
                                                    <Eye
                                                        size={
                                                            16
                                                        }
                                                    />
                                                    View
                                                </button>

                                            </td>

                                        </tr>
                                    )
                                )
                            )}

                        </tbody>

                    </table>

                </div>

            </div>

            {/* =================================================
                VIEW / VERIFY MODAL
            ================================================= */}

            {showModal &&
                selectedProfile && (
                    <div
                        className="verification-modal-overlay"
                        onMouseDown={(e) => {
                            if (
                                e.target ===
                                e.currentTarget
                            ) {
                                closeModal();
                            }
                        }}
                    >

                        <div className="verification-modal">

                            {/* HEADER */}

                            <div className="verification-modal-header">

                                <div>
                                    <h2>
                                        Profile Verification
                                    </h2>

                                    <p>
                                        {
                                            selectedProfile.memberId
                                        }
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    onClick={
                                        closeModal
                                    }
                                >
                                    <X size={20} />
                                </button>

                            </div>

                            {/* BODY */}

                            <div className="verification-modal-body">

                                {/* PROFILE HEADER */}

                                <div className="verification-profile-card">

                                    <div className="verification-large-avatar">
                                        <UserRound
                                            size={30}
                                        />
                                    </div>

                                    <div className="verification-profile-info">

                                        <h3>
                                            {
                                                selectedProfile.name
                                            }
                                        </h3>

                                        <span>
                                            {
                                                selectedProfile.memberId
                                            }
                                        </span>

                                        <div className="verification-profile-tags">

                                            <span>
                                                {
                                                    selectedProfile.gender
                                                }
                                            </span>

                                            <span>
                                                {
                                                    selectedProfile.community
                                                }
                                            </span>

                                            <span>
                                                {
                                                    selectedProfile.branch
                                                }
                                            </span>

                                        </div>

                                    </div>

                                    <span
                                        className={`verification-status-badge ${selectedProfile.profileStatus.toLowerCase()}`}
                                    >
                                        <span />
                                        {
                                            selectedProfile.profileStatus
                                        }
                                    </span>

                                </div>

                                {/* MEMBER INFORMATION */}

                                <div className="verification-section">

                                    <div className="verification-section-title">

                                        <div>
                                            <UserRound
                                                size={
                                                    17
                                                }
                                            />
                                        </div>

                                        <h3>
                                            Member Information
                                        </h3>

                                    </div>

                                    <div className="verification-detail-grid">

                                        <DetailItem
                                            label="Member ID"
                                            value={
                                                selectedProfile.memberId
                                            }
                                        />

                                        <DetailItem
                                            label="Name"
                                            value={
                                                selectedProfile.name
                                            }
                                        />

                                        <DetailItem
                                            label="Mobile"
                                            value={
                                                selectedProfile.mobile
                                            }
                                        />

                                        <DetailItem
                                            label="Email"
                                            value={
                                                selectedProfile.email
                                            }
                                        />

                                        <DetailItem
                                            label="Date of Birth"
                                            value={
                                                selectedProfile.dob
                                            }
                                        />

                                        <DetailItem
                                            label="Community"
                                            value={
                                                selectedProfile.community
                                            }
                                        />

                                        <DetailItem
                                            label="Branch"
                                            value={
                                                selectedProfile.branch
                                            }
                                        />

                                        <DetailItem
                                            label="Submitted Date"
                                            value={
                                                selectedProfile.submittedDate
                                            }
                                        />

                                    </div>

                                </div>

                                {/* AADHAAR */}

                                <div className="verification-section">

                                    <div className="verification-section-title">

                                        <div className="aadhaar-icon">
                                            <ShieldCheck
                                                size={
                                                    17
                                                }
                                            />
                                        </div>

                                        <h3>
                                            Aadhaar Verification
                                        </h3>

                                    </div>

                                    <div className="aadhaar-verification-card">

                                        <div className="aadhaar-number-box">

                                            <span>
                                                Aadhaar Number
                                            </span>

                                            <strong>
                                                XXXX XXXX{" "}
                                                {
                                                    selectedProfile.aadhaarLast4
                                                }
                                            </strong>

                                            <small>
                                                Full Aadhaar
                                                number is
                                                intentionally
                                                masked.
                                            </small>

                                        </div>

                                        <div className="aadhaar-status-box">

                                            <span>
                                                Verification
                                                Status
                                            </span>

                                            <span
                                                className={`verification-status-badge ${selectedProfile.aadhaarStatus.toLowerCase()}`}
                                            >
                                                <span />
                                                {
                                                    selectedProfile.aadhaarStatus
                                                }
                                            </span>

                                            {selectedProfile.aadhaarVerifiedDate && (
                                                <small>
                                                    Verified on{" "}
                                                    {
                                                        selectedProfile.aadhaarVerifiedDate
                                                    }
                                                </small>
                                            )}

                                        </div>

                                    </div>

                                    {/* DOCUMENT */}

                                    <div className="aadhaar-document-area">

                                        <div className="aadhaar-document-header">

                                            <div>
                                                <h4>
                                                    Aadhaar
                                                    Document
                                                </h4>

                                                <p>
                                                    Uploaded
                                                    verification
                                                    document
                                                </p>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setShowDocument(
                                                        (
                                                            prev
                                                        ) =>
                                                            !prev
                                                    )
                                                }
                                            >
                                                <Eye
                                                    size={
                                                        15
                                                    }
                                                />
                                                {showDocument
                                                    ? "Hide"
                                                    : "View Document"}
                                            </button>

                                        </div>

                                        {showDocument && (
                                            <div className="aadhaar-document-preview">

                                                <img
                                                    src={
                                                        selectedProfile.aadhaarDocument
                                                    }
                                                    alt="Aadhaar document"
                                                />

                                            </div>
                                        )}

                                    </div>

                                </div>

                                {/* PROFILE COMPLETION */}

                                <div className="verification-section">

                                    <div className="verification-section-title">

                                        <div>
                                            <FileCheck2
                                                size={
                                                    17
                                                }
                                            />
                                        </div>

                                        <h3>
                                            Profile Completion
                                        </h3>

                                    </div>

                                    <div className="profile-completion-large">

                                        <div className="completion-large-header">

                                            <strong>
                                                {
                                                    selectedProfile.profileCompletion
                                                }
                                                %
                                            </strong>

                                            <span>
                                                Profile
                                                Completed
                                            </span>

                                        </div>

                                        <div className="completion-large-bar">
                                            <div
                                                style={{
                                                    width: `${selectedProfile.profileCompletion}%`,
                                                }}
                                            />
                                        </div>

                                    </div>

                                </div>

                                {/* REJECTION */}

                                {selectedProfile.profileStatus ===
                                    "Rejected" && (
                                    <div className="previous-rejection">

                                        <div>
                                            <AlertTriangle
                                                size={
                                                    18
                                                }
                                            />
                                        </div>

                                        <div>
                                            <strong>
                                                Previous
                                                Rejection
                                            </strong>

                                            <p>
                                                {
                                                    selectedProfile.rejectionReason
                                                }
                                            </p>
                                        </div>

                                    </div>
                                )}

                                {/* REJECTION FORM */}

                                {showRejectBox && (
                                    <div className="reject-form">

                                        <label>
                                            Rejection
                                            Reason
                                        </label>

                                        <textarea
                                            value={
                                                rejectionReason
                                            }
                                            onChange={(
                                                e
                                            ) =>
                                                setRejectionReason(
                                                    e
                                                        .target
                                                        .value
                                                )
                                            }
                                            placeholder="Enter reason for rejecting this profile..."
                                            rows="4"
                                        />

                                        <div className="reject-form-actions">

                                            <button
                                                type="button"
                                                className="cancel-small-btn"
                                                onClick={() =>
                                                    setShowRejectBox(
                                                        false
                                                    )
                                                }
                                            >
                                                Cancel
                                            </button>

                                            <button
                                                type="button"
                                                className="confirm-reject-btn"
                                                onClick={
                                                    rejectProfile
                                                }
                                            >
                                                <XCircle
                                                    size={
                                                        16
                                                    }
                                                />
                                                Confirm
                                                Rejection
                                            </button>

                                        </div>

                                    </div>
                                )}

                                {/* HISTORY */}

                                <div className="verification-section">

                                    <div className="verification-section-title">

                                        <div>
                                            <CalendarDays
                                                size={
                                                    17
                                                }
                                            />
                                        </div>

                                        <h3>
                                            Verification
                                            History
                                        </h3>

                                    </div>

                                    <div className="verification-history">

                                        {selectedProfile.verificationHistory?.map(
                                            (
                                                history,
                                                index
                                            ) => (
                                                <div
                                                    className="history-item"
                                                    key={
                                                        index
                                                    }
                                                >

                                                    <div
                                                        className={`history-dot ${history.status.toLowerCase()}`}
                                                    />

                                                    <div className="history-content">

                                                        <strong>
                                                            {
                                                                history.action
                                                            }
                                                        </strong>

                                                        <span>
                                                            {
                                                                history.date
                                                            }{" "}
                                                            •{" "}
                                                            {
                                                                history.by
                                                            }
                                                        </span>

                                                    </div>

                                                </div>
                                            )
                                        )}

                                    </div>

                                </div>

                            </div>

                            {/* FOOTER */}

                            <div className="verification-modal-footer">

                                <button
                                    type="button"
                                    className="verification-close-btn"
                                    onClick={
                                        closeModal
                                    }
                                >
                                    Close
                                </button>

                                <button
                                    type="button"
                                    className="verification-reject-btn"
                                    onClick={() =>
                                        setShowRejectBox(
                                            true
                                        )
                                    }
                                >
                                    <XCircle
                                        size={16}
                                    />
                                    Reject
                                </button>

                                <button
                                    type="button"
                                    className="verification-aadhaar-btn"
                                    onClick={
                                        verifyAadhaar
                                    }
                                    disabled={
                                        selectedProfile.aadhaarStatus ===
                                        "Verified"
                                    }
                                >
                                    <ShieldCheck
                                        size={16}
                                    />

                                    {selectedProfile.aadhaarStatus ===
                                    "Verified"
                                        ? "Aadhaar Verified"
                                        : "Verify Aadhaar"}
                                </button>

                                <button
                                    type="button"
                                    className="verification-approve-btn"
                                    onClick={
                                        approveProfile
                                    }
                                >
                                    <Check
                                        size={17}
                                    />
                                    Approve Profile
                                </button>

                            </div>

                        </div>

                    </div>
                )}

        </div>
    );
}

/* =========================================================
   DETAIL ITEM
========================================================= */

function DetailItem({
    label,
    value,
}) {
    return (
        <div className="verification-detail-item">

            <span>{label}</span>

            <strong>
                {value || "-"}
            </strong>

        </div>
    );
}

export default ProfileVerification;